import type { Event } from "@lezz/shared";
import { haversineDistanceKm } from "@/shared/lib/utils";
import type { SortOption } from "@/features/events/components/sort-select";

type SortableEvent = Event & { participantCount?: number };

/**
 * Compares two date strings for sorting.
 * @param dateA - First date string
 * @param dateB - Second date string
 * @param ascending - If true, sorts ascending (earliest first), otherwise descending (latest first)
 * @returns Comparison result: negative if A comes before B, positive if A comes after B, 0 if equal
 */
export function compareDates(
  dateA: string,
  dateB: string,
  ascending = false
): number {
  const timestampA = new Date(dateA).getTime();
  const timestampB = new Date(dateB).getTime();

  if (isNaN(timestampA) && isNaN(timestampB)) return 0;
  if (isNaN(timestampA)) return 1;
  if (isNaN(timestampB)) return -1;

  return ascending ? timestampA - timestampB : timestampB - timestampA;
}

/**
 * Sorts events for the events list. "popular" reads `participantCount`
 * straight off the event object (embedded by `/api/events`) instead of the
 * React Query cache, so the order is correct on first paint before any card
 * has mounted.
 */
export function sortEvents<T extends SortableEvent>(
  events: T[],
  sortBy: SortOption,
  location?: { lat: number; lng: number } | null
): T[] {
  const sorted = [...events];

  if (sortBy === "date") {
    return sorted.sort((a, b) =>
      compareDates(a.dateAndTime, b.dateAndTime, true)
    );
  }

  if (sortBy === "popular") {
    return sorted.sort((a, b) => {
      const countA = a.participantCount ?? 0;
      const countB = b.participantCount ?? 0;

      if (countA !== countB) {
        return countB - countA; // Descending
      }

      // Secondary sort by date (descending to match primary date sort behavior)
      return compareDates(a.dateAndTime, b.dateAndTime, false);
    });
  }

  if (sortBy === "distance") {
    return sorted.sort((a, b) => {
      if (!location) return 0;
      const distA =
        a.lat != null && a.lng != null
          ? haversineDistanceKm(location.lat, location.lng, a.lat, a.lng)
          : Infinity;
      const distB =
        b.lat != null && b.lng != null
          ? haversineDistanceKm(location.lat, location.lng, b.lat, b.lng)
          : Infinity;
      return distA - distB;
    });
  }

  return sorted;
}
